<template>
  <div class="ray-chase-wrapper">
    <svg
      viewBox="0 0 200 200"
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      class="ray-chase-svg">
      <defs>
        <radialGradient
          :id="`centerHaloGradient-${uid}`"
          cx="100"
          cy="100"
          r="100"
          gradientUnits="userSpaceOnUse">
          <stop offset="0%" stop-color="#FFD700" stop-opacity="1" />
          <stop offset="30%" stop-color="#FFD700" stop-opacity="1" />
          <stop offset="35%" stop-color="#FFD700" stop-opacity="0.6" />
          <stop offset="50%" stop-color="#FFD700" stop-opacity="0" />
        </radialGradient>
        <radialGradient
          :id="`rayChaseGradient-${uid}`"
          cx="100"
          cy="100"
          r="100"
          gradientUnits="userSpaceOnUse">
          <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.8" />
          <stop offset="30%" stop-color="#FFD700" />
          <stop offset="100%" stop-color="#FFD700" stop-opacity="0" />
        </radialGradient>

        <filter :id="`starGlow-${uid}`" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.3" />
        </filter>

        <path id="singleChaseRayShape" d="M100 100 L94 0 L106 0 Z" />

        <path
          id="crossStarShape"
          d="M0 -1.5 L-0.3 -0.3 L-1.5 0 L-0.3 0.3 L0 1.5 L0.3 0.3 L1.5 0 L0.3 -0.3 Z" />

        <radialGradient :id="`dotGradient-${uid}`" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
          <stop offset="0%" stop-color="#FFFFFF" stop-opacity="1" />
          <stop offset="30%" stop-color="#FFD700" stop-opacity="0.9" />
          <stop offset="100%" stop-color="#FFD700" stop-opacity="0" />
        </radialGradient>
      </defs>
      <circle
        cx="100"
        cy="100"
        r="100"
        :fill="`url(#centerHaloGradient-${uid})`"
        class="center-halo-layer" />

      <g class="stars-group" v-if="props.particleType !== 'none'">
        <g
          v-for="(p, index) in particlesData"
          :key="`p-group-${index}`"
          :transform="`translate(${p.x}, ${p.y})`">
          <!-- 十字星模式 -->
          <template v-if="props.particleType === 'star'">
            <use
              v-if="p.isGlowing"
              href="#crossStarShape"
              class="star-item"
              :transform="`scale(${p.scale})`"
              :style="{
                animationDelay: p.delay + 's',
                opacity: p.baseOpacity,
                color: '#fffacd',
                filter: `url(#starGlow-${uid})`
              }" />
            <circle
              v-else
              cx="0"
              cy="0"
              :r="p.r"
              class="star-item"
              :style="{
                animationDelay: p.delay + 's',
                opacity: p.baseOpacity,
                color: '#ffffff'
              }" />
          </template>

          <!-- 圆点模式 -->
          <template v-else-if="props.particleType === 'dot'">
            <circle
              cx="0"
              cy="0"
              :r="p.r"
              class="star-item"
              :style="{
                animationDelay: p.delay + 's',
                opacity: p.baseOpacity,
                fill: `url(#dotGradient-${uid})`
              }" />
          </template>
        </g>
      </g>

      <g class="rays-group">
        <use
          v-for="(ray, index) in raysData"
          :key="`ray-${index}`"
          href="#singleChaseRayShape"
          class="ray-item"
          :style="{
            animationDelay: ray.delay + 's',
            fill: `url(#rayChaseGradient-${uid})`
          }"
          :transform="`rotate(${ray.rotation} 100 100)`" />
      </g>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  mode?: 'full' | 'half'
  particleType?: 'star' | 'dot' | 'none'
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'full',
  particleType: 'star'
})

// 生成唯一ID，避免多个组件实例间SVG ID冲突
const uid = Math.random().toString(36).substr(2, 9)

const CONFIG = {
  half: {
    rayCount: 13,
    starCount: 20,
    angleRange: [-80, 80]
  },
  full: {
    rayCount: 26,
    starCount: 40,
    angleRange: [0, 360]
  },
  rayDuration: 10,
  starDuration: 4
} as const

const currentConfig = computed(() => CONFIG[props.mode])

// --- 射线计算逻辑 ---
const raysData = computed(() => {
  const { rayCount } = currentConfig.value
  const { rayDuration } = CONFIG

  // Half mode logic
  if (props.mode === 'half') {
    const [startAngle, endAngle] = CONFIG.half.angleRange
    const totalAngle = endAngle - startAngle
    const angleStep = totalAngle / (rayCount - 1)

    return Array.from({ length: rayCount }, (_, i) => ({
      rotation: startAngle + angleStep * i,
      delay: (Math.random() * rayDuration).toFixed(2)
    }))
  }

  // Full mode logic
  const angleStep = 360 / rayCount
  return Array.from({ length: rayCount }, (_, i) => ({
    rotation: angleStep * i,
    delay: (Math.random() * rayDuration).toFixed(2)
  }))
})

// --- 粒子/星星计算逻辑 ---
const particlesData = computed(() => {
  if (props.particleType === 'none') return []

  const { starCount } = currentConfig.value
  const { starDuration } = CONFIG

  return Array.from({ length: starCount }, () => {
    const isLarge = Math.random() < 0.28
    const { x, y } = getRandomPosition(props.mode)

    return {
      x,
      y,
      scale: isLarge ? (Math.random() * 0.4 + 0.6).toFixed(2) : 1,
      r: getParticleRadius(isLarge, props.particleType),
      baseOpacity: getBaseOpacity(props.particleType),
      delay: (Math.random() * starDuration).toFixed(2),
      isGlowing: isLarge
    }
  })
})

/**
 * 获取随机坐标
 */
function getRandomPosition(mode: 'full' | 'half') {
  if (mode === 'half') {
    // 限制在上方区域: x(0-200), y(20-120)
    return {
      x: Math.random() * 200,
      y: Math.random() * 100 + 20
    }
  }
  return {
    x: Math.random() * 200,
    y: Math.random() * 200
  }
}

/**
 * 获取粒子基础透明度
 */
function getBaseOpacity(type: 'star' | 'dot' | 'none') {
  if (type === 'dot') return Math.random() * 0.3 + 0.7
  return Math.random() * 0.5 + 0.4
}

/**
 * 获取粒子半径
 */
function getParticleRadius(isLarge: boolean, type: 'star' | 'dot' | 'none') {
  if (type === 'dot') {
    // 调小圆点尺寸：小点 0.8-1.2，大点 1.5-2.5 (视觉上配合渐变边缘会显得更柔和)
    return !isLarge ? Math.random() * 0.4 + 0.8 : Math.random() * 1.0 + 1.3
  }
  // 默认 star 模式逻辑
  return !isLarge ? Math.random() * 0.4 + 0.2 : 0
}
</script>

<style scoped lang="scss">
.ray-chase-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

// 中心光晕 呼吸动画
.center-halo-layer {
  animation: haloBreathe 3s ease-in-out infinite alternate;
}

@keyframes haloBreathe {
  0% {
    opacity: 0.4;
    transform: scale(0.95);
    transform-origin: center;
  }
  100% {
    opacity: 0.65;
    transform: scale(1.02);
    transform-origin: center;
  }
}
/* --- 射线 --- */
.ray-item {
  opacity: 0.03;
  // fill通过style动态绑定ID引用，此处仅保留fallback或移除
  will-change: opacity, filter;
  animation: rayChaseAnim 4.5s infinite linear;
}

/* --- 星星 --- */
.star-item {
  fill: currentColor;
  will-change: opacity, transform;
  animation: starTwinkle 3s infinite ease-in-out;

  transform-origin: 0 0;
  transform-box: border-box;
}

/* --- 动画定义 --- */
@keyframes rayChaseAnim {
  0%,
  100% {
    opacity: 0.08;
    filter: none;
  }
  30% {
    opacity: 0.15;
    filter: drop-shadow(0 0 1px #ffd700) drop-shadow(0 0 5px #ffd700);
  }
  60% {
    opacity: 0.08;
    filter: none;
  }
}

@keyframes starTwinkle {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
</style>
